import { RefObject } from "react";
import { Button, Divider, Tag, Avatar, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBan } from "@fortawesome/pro-solid-svg-icons";
import { faQuestionCircle } from "@fortawesome/pro-duotone-svg-icons";
import { red } from "@ant-design/colors";
import { LeftOutlined } from "@ant-design/icons";
import { CrewMember, RouteJob } from "../routingApi";
import { truncateRouteName } from "./routeViewModel";
import { RouteViewState, hasRouteChanged } from "./routeViewModel";

export type RouteViewTitleRowProps = {
  state: RouteViewState
  routeJobs: RouteJob[] | null
  crew: CrewMember[] | undefined
  routeNameHeaderRef: RefObject<HTMLDivElement>
  onBackToList: () => void
  onAddJobsDrawerOpen: () => void
  onOpenSaveRouteModal: () => void
  onOpenTour: () => void
};

const RouteViewTitleRow = ({
  state,
  routeJobs,
  crew,
  routeNameHeaderRef,
  onBackToList,
  onAddJobsDrawerOpen,
  onOpenSaveRouteModal,
  onOpenTour
}: RouteViewTitleRowProps) => {

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <LeftOutlined onClick={onBackToList} />
        <Tooltip title={state?.route.name || 'Loading...'}>
          <div ref={routeNameHeaderRef} style={{ fontWeight: 600, fontSize: 18 }}>
            {state?.route.name ? truncateRouteName(state.route.name) : 'Loading...'}
          </div>
        </Tooltip>
        <Tooltip title="Need help? Take a quick tour of the route planner features">
          <Button 
            type="text"
            size="small"
            onClick={onOpenTour}
            style={{ padding: '4px' }}
          >
            <FontAwesomeIcon icon={faQuestionCircle} />
          </Button>
        </Tooltip>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Divider type="vertical" style={{ margin: '0 8px' }} />
        {state.route.crewMemberIds ? (
          <Avatar.Group
            size="small"
            maxCount={1}
            maxStyle={{
              opacity: 0.5,
              backgroundColor: '#faad14'
            }}
          >
            {!state.route.crewLeadId && (
              <Tooltip title={"No Crew Lead"}>
                <Avatar style={{ backgroundColor: 'white' }} icon={<FontAwesomeIcon icon={faBan} color={red.primary} />} />
              </Tooltip>
            )}
            {state.route.crewMemberIds.split(',')
              .map(id => {
                const member = crew?.find(c => c.id === parseInt(id));
                return member ? { id: parseInt(id), member } : null;
              })
              .filter((item): item is { id: number; member: CrewMember } => item !== null)
              .sort((a, b) => {
                if (a.id === state.route.crewLeadId) return -1;
                if (b.id === state.route.crewLeadId) return 1;
                return 0;
              })
              .map(({ member }) => (
                <Tooltip key={member.id} title={member.name}>
                  <Avatar src={member.avatar}>{member.name.charAt(0)}</Avatar>
                </Tooltip>
              ))}
          </Avatar.Group>
        ) : (
          <Avatar.Group
            size="small"
            maxCount={1}
            maxStyle={{
              backgroundColor: 'white',
            }}
          >
            <Tooltip title={"No Crew Members"}>
              <Avatar style={{ backgroundColor: 'white' }} icon={<FontAwesomeIcon icon={faBan} color={red.primary} />} />
            </Tooltip>
          </Avatar.Group>
        )}
        <Divider type="vertical" style={{ margin: '0 8px' }} />
        <Tag id="route-status-tag" color={state?.route.active ? "success" : "default"} style={{ borderRadius: '12px' }}>
          {state?.route.active ? "Active" : "Inactive"}
        </Tag>
        <Divider type="vertical" style={{ margin: '0 8px' }} />
        <Button id="manage-jobs-button" onClick={onAddJobsDrawerOpen}>+ Manage Jobs</Button>
        <Button 
          id="save-route-button"
          type="primary" 
          onClick={onOpenSaveRouteModal}
          disabled={!hasRouteChanged(state.jobs, routeJobs)}
        >
          Save Route
        </Button>
      </div>
    </div>
  );
};

export default RouteViewTitleRow;